import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, CheckCircle, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadCaptureForm() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, source: window.location.pathname });
      setSubmitted(true);
      toast.success("Your guide is on the way — check your inbox.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-[#3A4F41] rounded-3xl p-8 sm:p-12 text-center" data-testid="lead-capture-success">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
          <CheckCircle size={32} className="text-[#C87B5D]" />
        </div>
        <h3 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight mb-3">
          Thank You!
        </h3>
        <p className="font-body text-white/70 max-w-md mx-auto mb-6">
          Check your inbox — your guide is on the way. You can also download it now:
        </p>
        <a
          href="/guides/daily-guide-to-moving-better.pdf"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body text-sm font-medium px-6 py-3 transition-colors"
          data-testid="lead-capture-download-link"
        >
          <FileDown size={16} />
          Download the Daily Guide to Moving Better (PDF)
        </a>
      </div>
    );
  }

  return (
    <div className="bg-[#3A4F41] rounded-3xl p-8 sm:p-12" data-testid="lead-capture-form">
      <div className="max-w-xl mx-auto text-center">
        <h3 className="font-heading text-2xl sm:text-3xl font-medium text-white tracking-tight mb-3">
          Get My Daily Guide to Moving Better
        </h3>
        <p className="font-body text-white/70 mb-8">
          A short guide to moving better, from five of Dr. Greenspan's articles.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Input
              data-testid="lead-name"
              placeholder="Your name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="flex-1 rounded-xl border-white/20 bg-white/10 text-white placeholder:text-white/50 h-12 font-body focus-visible:ring-[#C87B5D]"
            />
            <Input
              data-testid="lead-email"
              type="email"
              placeholder="your@email.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="flex-1 rounded-xl border-white/20 bg-white/10 text-white placeholder:text-white/50 h-12 font-body focus-visible:ring-[#C87B5D]"
            />
          </div>
          <Button
            data-testid="lead-submit"
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto px-8 rounded-full bg-[#C87B5D] hover:bg-[#b56a4e] text-white font-body h-12"
          >
            {loading ? "Sending..." : (
              <>
                <Send size={16} className="mr-2" />
                Send Me the Guide
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
